// The dev push panel behind `/debug` (the Push tab): search any person, list their registered devices, and send
// them a test push.
//
// Raw fetch rather than the generated client, for the reason `useSongstatsDebug.ts` gives: the controller is
// `[ApiExplorerSettings(IgnoreApi)]`, so these routes are deliberately absent from `openapi.json` and the shapes are
// hand-mirrored from `PushDebugService.cs`. The routes only exist in non-production hosts.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

export interface PushDebugPerson {
  personId: string;
  name: string;
  email: string;
  status: string;
}

export interface PushDebugDevice {
  pushTokenId: string;
  platform: string;
  appEnv: string;
  enabled: boolean;
  /** The token's last eight characters — never the whole token. */
  tokenTail: string;
  deviceModel: string | null;
  appVersion: string | null;
  createdOn: string;
  lastSeenOn: string | null;
}

export interface PushDebugDevices {
  providerConfigured: boolean;
  person: PushDebugPerson;
  devices: PushDebugDevice[];
}

export interface PushDebugSendRequest {
  personId: string;
  title: string;
  body: string;
  kind: string;
  route: string;
  viaWorker: boolean;
}

export interface PushDebugDeviceResult {
  pushTokenId: string;
  tokenTail: string;
  /** `Sent` | `Unregistered` | `TransientFailure` | `PermanentFailure` | `ProviderUnconfigured`. */
  outcome: string;
  reasonCode: string | null;
  environmentUsed: string;
}

export interface PushDebugSendResult {
  viaWorker: boolean;
  providerConfigured: boolean;
  queued: boolean;
  detail: string;
  attempted: number;
  sent: number;
  disabled: number;
  failed: number;
  transient: number;
  devices: PushDebugDeviceResult[];
}

const pushUrl = (bandId: string, suffix: string) => `${API_URL}/api/v1/bands/${bandId}/debug/push${suffix}`;

/** A raw authenticated call, bearer from the TokenProvider (never `localStorage`), `credentials: "include"`. */
async function pushFetch<T>(bearer: string | null, url: string, init?: { method: "POST"; body: unknown }): Promise<T> {
  const headers: Record<string, string> = bearer ? { Authorization: `Bearer ${bearer}` } : {};
  if (init) headers["Content-Type"] = "application/json";
  const response = await fetch(url, {
    method: init?.method ?? "GET",
    credentials: "include",
    headers,
    body: init ? JSON.stringify(init.body) : undefined,
  });
  if (!response.ok) {
    let problem: unknown;
    try {
      problem = await response.json();
    } catch {
      problem = undefined;
    }
    throw new ApiError(response.status, problem);
  }
  const body = (await response.json()) as { data?: T };
  if (body?.data === undefined) throw new ApiError(response.status, undefined);
  return body.data;
}

const KEY = ["push-debug"] as const;

/** People matching `query` (name or email). Runs from two characters; anything shorter answers empty server-side. */
export function usePushDebugPeople(query: string) {
  const { tokenProvider } = useApp();
  const { bandId } = useBandContext();
  const q = query.trim();
  return useQuery({
    queryKey: ["band", bandId, ...KEY, "people", q],
    queryFn: () =>
      pushFetch<PushDebugPerson[]>(tokenProvider.getBearer(), pushUrl(bandId, `/people?q=${encodeURIComponent(q)}`)),
    enabled: q.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
}

/** Every device the person has registered, enabled or not. */
export function usePushDebugDevices(personId: string | null) {
  const { tokenProvider } = useApp();
  const { bandId } = useBandContext();
  return useQuery({
    queryKey: ["band", bandId, ...KEY, "devices", personId],
    queryFn: () =>
      pushFetch<PushDebugDevices>(tokenProvider.getBearer(), pushUrl(bandId, `/people/${personId}/devices`)),
    enabled: personId !== null,
    staleTime: 0,
    retry: false,
  });
}

/** Send a test push. Refreshes the device list afterwards — a 410 soft-disables a token, a sandbox retry flips one. */
export function usePushDebugSend() {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<PushDebugSendResult, PushDebugSendRequest>(
    (band, body) =>
      pushFetch<PushDebugSendResult>(tokenProvider.getBearer(), pushUrl(band, "/send"), { method: "POST", body }),
    {
      onSuccess: (_, body) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, ...KEY, "devices", body.personId] }),
    },
  );
}
